import {
  DeviceType,
  EAppSupportType,
  EPropertyType,
  ESymbolCategory,
  IProperty,
  IPropertyEnums,
  IPropertyTypeOptions,
  ISymbol,
  ReactTypes
} from "@kuika/kuika-cl-model";
import * as React from "react";
import { IWithContextProp, withContext } from "../../../../shared/hoc/with-context";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { OnPressKeyRules } from "../../../../shared/utility/on-press-key-rules";
import { KModal } from "../../../ui-kit/k-modal/k-modal";
import { KModalFooter } from "../../../ui-kit/k-modal/k-modal-footer";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaCheckbox from "../../../ui-kit/kuika-checkbox/kuika-checkbox";
import KuikaInput from "../../../ui-kit/kuika-input/kuika-input";
import KuikaListTable, { KuikaListTableColumn } from "../../../ui-kit/kuika-list-table/kuika-list-table";
import KuikaSelectbox, { KuikaSelectboxOption } from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaSwitch from "../../../ui-kit/kuika-switch/kuika-switch";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallBoldText from "../../../ui-kit/typography/SmallBoldText/SmallBoldText";
import KuikaSmallText from "../../../ui-kit/typography/SmallText/SmallText";
import "./component-properties-modal.scss";

/**
 * A property definition value as it is persisted into the component set JSON.
 * Deliberately narrow: this modal never writes anything else onto a property.
 */
type PropertyDefinitionValue = string | boolean | number | undefined;

/**
 * The non-boolean fields this modal edits. Spelled out because the write path is
 * key-driven and a typo would mint a junk key into the component set JSON.
 * `value` is the odd one out — it is stored as the property's symbol.
 */
type PropertyTextKey = "name" | "tagName" | "description" | "value";

/**
 * The 21 "which options bag is this property?" markers.
 *
 * They are mutually exclusive by construction, not by convention: every consumer
 * dispatches on them with a first-match-wins chain
 * (`property-helper.ts` `calculatePropertyToolType`, `React_UIGenerator.cs`'s
 * `if / else if` ladder, `component-metadata.ts` `OPTIONS_BAG_FLAG_TO_KIND`), so a
 * second flag set on the same property is silently dropped rather than honoured.
 * They are therefore asked as one "Data role" selector instead of 21 toggles.
 *
 * Order is the `IPropertyDefinition` declaration order — it is what decides which
 * role is displayed for the (non-existent in live data) case of two flags set at
 * once, and mirroring one of the consumers' inconsistent chain orders would be
 * arbitrary.
 */
type DataRoleFlagKey =
  | "isOptionsProp"
  | "isLanguageOptionsProp"
  | "isGoogleMapsOptionsProp"
  | "isTableOptionsProp"
  | "isChatOptionsProp"
  | "isTreeOptionsProp"
  | "isChartOptionsProp"
  | "isTimelineOptionsProp"
  | "isCalendarOptionsProp"
  | "isMenuOptionsProp"
  | "isBreadcrumbOptionsProp"
  | "isRadialChartOptionsProp"
  | "isRadialChartLevelOptionsProp"
  | "isMixedChartOptionsProp"
  | "isMixedChartLineOptionsProp"
  | "isMixedChartPointOptionsProp"
  | "isMixedChartBarOptionsProp"
  | "isKanbanBoardLaneOptionsProp"
  | "isKanbanBoardCardOptionsProp"
  | "isNodeTreeNodeOptionsProp"
  | "isDirectoryOptionsProp";

interface DataRoleFlag {
  key: DataRoleFlagKey;
  label: string;
}

const DATA_ROLE_NONE = "none";

const DATA_ROLE_FLAGS: DataRoleFlag[] = [
  { key: "isOptionsProp", label: "Options" },
  { key: "isLanguageOptionsProp", label: "Language Options" },
  { key: "isGoogleMapsOptionsProp", label: "Google Maps Options" },
  { key: "isTableOptionsProp", label: "Table Options" },
  { key: "isChatOptionsProp", label: "Chat Options" },
  { key: "isTreeOptionsProp", label: "Tree Options" },
  { key: "isChartOptionsProp", label: "Chart Options" },
  { key: "isTimelineOptionsProp", label: "Timeline Options" },
  { key: "isCalendarOptionsProp", label: "Calendar Options" },
  { key: "isMenuOptionsProp", label: "Menu Options" },
  { key: "isBreadcrumbOptionsProp", label: "Breadcrumb Options" },
  { key: "isRadialChartOptionsProp", label: "Radial Chart Options" },
  { key: "isRadialChartLevelOptionsProp", label: "Radial Chart Level Options" },
  { key: "isMixedChartOptionsProp", label: "Mixed Chart Options" },
  { key: "isMixedChartLineOptionsProp", label: "Mixed Chart Line Options" },
  { key: "isMixedChartPointOptionsProp", label: "Mixed Chart Point Options" },
  { key: "isMixedChartBarOptionsProp", label: "Mixed Chart Bar Options" },
  { key: "isKanbanBoardLaneOptionsProp", label: "Kanban Board Lane Options" },
  { key: "isKanbanBoardCardOptionsProp", label: "Kanban Board Card Options" },
  { key: "isNodeTreeNodeOptionsProp", label: "Node Tree Node Options" },
  { key: "isDirectoryOptionsProp", label: "Directory Options" }
];

/**
 * The flags that are NOT part of the data role. Each is read independently of the
 * others — none of them participates in the options-bag dispatch chains — so they
 * stay as toggles and are only grouped by what they affect.
 *
 * `isValueProp` in particular is set membership, not a slot: a single component
 * regularly carries it on several properties (Calendar has 7, Table 4), and
 * consumers read it with `.filter()` / `.Where()`.
 */
type PropertyToggleKey =
  | "isValueProp"
  | "isVisibleProp"
  | "isEditabilityProp"
  | "isVisibleInFieldPicker"
  | "hideFixedValue"
  | "isTechnical"
  | "isAdvanced"
  | "isIgnoredOnCodeOutput"
  | "isIgnoredOnGeneratedApp"
  | "isRequired"
  | "isSupportMultiLanguage"
  | "isDeprecated";

interface PropertyToggle {
  key: PropertyToggleKey;
  label: string;
  description: string;
}

interface PropertyToggleGroup {
  title: string;
  toggles: PropertyToggle[];
}

const PROPERTY_TOGGLE_GROUPS: PropertyToggleGroup[] = [
  {
    title: "Binding semantics",
    toggles: [
      {
        key: "isValueProp",
        label: "Is value prop?",
        description: "The property that carries the component's bound value. A component may have several."
      },
      {
        key: "isVisibleProp",
        label: "Is visible prop?",
        description: "The property that controls the component's runtime visibility."
      },
      {
        key: "isEditabilityProp",
        label: "Is editability prop?",
        description: "The property that controls whether the component is editable."
      }
    ]
  },
  {
    title: "Symbol picker",
    toggles: [
      {
        key: "isVisibleInFieldPicker",
        label: "Is visible in symbol picker?",
        description: "Offer this property when the user picks a field to bind."
      },
      {
        key: "hideFixedValue",
        label: "Hide fixed value from symbol picker",
        description: "Remove the Fixed Value entry from this property's symbol picker."
      }
    ]
  },
  {
    title: "Designer visibility",
    toggles: [
      {
        key: "isTechnical",
        label: "Hide in designer",
        description: "Technical property — hidden from the properties panel of a placed component."
      },
      {
        key: "isAdvanced",
        label: "Is it advanced property",
        description: "Shown under the advanced section instead of the main list."
      }
    ]
  },
  {
    title: "Code generation",
    toggles: [
      {
        key: "isIgnoredOnCodeOutput",
        label: "Ignore on code output",
        description: "Never emitted into generated source."
      },
      {
        key: "isIgnoredOnGeneratedApp",
        label: "Ignore on generated app",
        description: "Excluded from the generated app's data binding props."
      }
    ]
  },
  {
    title: "Authoring",
    toggles: [
      { key: "isRequired", label: "Is required", description: "A value must be supplied before the set can be saved." },
      {
        key: "isSupportMultiLanguage",
        label: "Support Multi-Language",
        description: "Emitted through the translation helper in the generated app."
      },
      {
        key: "isDeprecated",
        label: "Is Deprecated",
        description: "Kept for existing apps but no longer offered for new work."
      }
    ]
  }
];

const APP_SUPPORT_TYPE_OPTIONS: KuikaSelectboxOption[] = [
  { value: EAppSupportType.All, label: "All" },
  { value: EAppSupportType.Web, label: "Web" },
  { value: EAppSupportType.Mobile, label: "Mobile" },
  { value: EAppSupportType.WebAndReactNative, label: "Web + React Native" }
];

interface DeviceChoice {
  device: DeviceType;
  label: string;
  icon: string;
}

const DEVICE_CHOICES: DeviceChoice[] = [
  { device: DeviceType.Phone, label: "Mobile", icon: "device-mobile" },
  { device: DeviceType.Tablet, label: "Tablet", icon: "device-tablet" },
  { device: DeviceType.Desktop, label: "Desktop", icon: "device-desktop" }
];

export interface ComponentPropertiesModalState {
  enumKey: string;
  enumValue: string;
}

export interface ComponentPropertiesModalProps {
  closeModal: () => void;
  showModal: boolean;
  p: IProperty;
  idx: number;
  properties: IProperty[];
  triggerPropertiesSetState: (properties: IProperty[]) => void;
}

class ComponentPropertiesModal extends React.PureComponent<
  ComponentPropertiesModalProps & IWithContextProp,
  ComponentPropertiesModalState
> {
  constructor(props: ComponentPropertiesModalProps & IWithContextProp) {
    super(props);
    this.state = { enumKey: "", enumValue: "" };
  }

  closeModal = (): void => {
    this.props.closeModal();
  };

  /**
   * The single write path for a property definition field. `value` is the only
   * property whose edit is stored indirectly — it replaces the property's symbol.
   */
  writePropertyValue = (index: number, key: string, value: PropertyDefinitionValue): void => {
    const updatedProperties: IProperty[] = [...this.props.properties];
    if (key === "value") {
      updatedProperties[index].symbol = {
        category: ESymbolCategory.fixed,
        fixedValue: value,
        label: "Value",
        description: "Fixed Value"
      } as ISymbol;
    } else {
      (updatedProperties[index] as unknown as Record<string, PropertyDefinitionValue>)[key] = value;
    }
    this.props.triggerPropertiesSetState(updatedProperties);
  };

  handlePropertyTextChange = (index: number, key: PropertyTextKey, value: string): void => {
    this.writePropertyValue(index, key, value);
  };

  handlePropertyToggleChange = (index: number, key: PropertyToggleKey, checked: boolean): void => {
    this.writePropertyValue(index, key, checked);
  };

  handleAppSupportTypeChange = (index: number, appSupportType: EAppSupportType): void => {
    this.writePropertyValue(index, "appSupportType", appSupportType);
  };

  /**
   * The role flag currently in effect, or `DATA_ROLE_NONE`. When more than one is
   * set — only ever true of legacy definitions — the first in declaration order
   * wins and the extra ones are surfaced as a warning rather than discarded.
   */
  getActiveDataRoleFlags = (p: IProperty): DataRoleFlag[] => {
    return DATA_ROLE_FLAGS.filter((flag) => p[flag.key] === true);
  };

  getSelectedDataRole = (p: IProperty): DataRoleFlagKey | typeof DATA_ROLE_NONE => {
    const active: DataRoleFlag[] = this.getActiveDataRoleFlags(p);
    if (active.length === 0) return DATA_ROLE_NONE;
    return active[0].key;
  };

  /**
   * Writes the role minimally: the newly chosen flag becomes `true` and only the
   * flags that were already `true` become `false`. Writing `false` across all 21
   * would mint 21 explicit keys into every property object merely opened here,
   * changing the shape of the persisted component set for no behavioural reason.
   */
  handleDataRoleChange = (index: number, nextRole: string): void => {
    const isKnownRole: boolean = nextRole === DATA_ROLE_NONE || DATA_ROLE_FLAGS.some((flag) => flag.key === nextRole);
    if (!isKnownRole) return;
    const updatedProperties: IProperty[] = [...this.props.properties];
    const target: Record<string, PropertyDefinitionValue> = updatedProperties[index] as unknown as Record<
      string,
      PropertyDefinitionValue
    >;
    this.getActiveDataRoleFlags(updatedProperties[index]).forEach((flag: DataRoleFlag): void => {
      if (flag.key !== nextRole) {
        target[flag.key] = false;
      }
    });
    if (nextRole !== DATA_ROLE_NONE) {
      target[nextRole] = true;
    }
    this.props.triggerPropertiesSetState(updatedProperties);
  };

  getPropertySymbolValue = (p: IProperty): string => {
    if (p.symbol) return String(p.symbol.fixedValue ?? "");
    if (p.value) return String(p.value);
    return "";
  };

  handlePropertySelectedTypeChange = (idx: number, selectedType: EPropertyType): void => {
    const properties: IProperty[] = [...this.props.properties];
    properties[idx] = { ...properties[idx], selectedType };
    this.props.triggerPropertiesSetState(properties);
  };

  handleTypeChange = (i: number, propertyType: EPropertyType, checked: boolean): void => {
    const properties: IProperty[] = [...this.props.properties];
    let selTypes: EPropertyType[] = [];
    const curPropery: IProperty = properties[i];
    if (curPropery) {
      selTypes = [...curPropery.type];
    }
    const idx: number = selTypes.indexOf(propertyType);
    if (idx === -1 && checked === true) {
      selTypes.push(propertyType);
    } else if (checked === false && idx > -1) {
      selTypes.splice(idx, 1);
    }
    const currentTypeOptions: IPropertyTypeOptions[] = [];
    selTypes.forEach((sT: EPropertyType): void => {
      const selectedType: IPropertyTypeOptions | undefined = properties[i].typeOptions.find(
        (c) => Number(c.propertyType) === sT
      );
      if (!selectedType) {
        currentTypeOptions.push({
          propertyType: sT.toString(),
          hasOptions: false,
          options: [],
          jsonSchema: "",
          hasOwnJSONSchema: false
        });
      } else {
        currentTypeOptions.push(selectedType);
      }
    });
    properties[i] = {
      ...properties[i],
      type: selTypes,
      typeOptions: currentTypeOptions
    };
    if (properties[i].type && properties[i].type.length === 1) {
      properties[i].selectedType = properties[i].type[0];
    }
    this.props.triggerPropertiesSetState(properties);
  };

  getPropertyType = (item: IPropertyTypeOptions): string => {
    if (item.propertyType === EPropertyType.enum.toString()) return "enum";
    if (item.propertyType === EPropertyType.object.toString()) return "json";
    if (item.propertyType === EPropertyType.column.toString()) return "column";
    return "other";
  };

  handleDeviceChangedForColumnAttribute = (device: DeviceType, idx: number, item: IPropertyTypeOptions): void => {
    const properties: IProperty[] = [...this.props.properties];
    const { typeOptions } = properties[idx];
    const selectedType: IPropertyTypeOptions | undefined = typeOptions.find(
      (t) => t.propertyType === item.propertyType
    );
    if (selectedType) {
      selectedType.deviceTypeForColumnAttribute = device;
    }
    properties[idx] = { ...properties[idx] };
    this.props.triggerPropertiesSetState(properties);
  };

  handleEnumKeyChange = (value: string): void => {
    this.setState({ enumKey: value });
  };

  handleEnumValueChange = (value: string): void => {
    this.setState({ enumValue: value });
  };

  /**
   * Enum edits go through the same state channel as every other field. They used
   * to mutate `typeOptions[n].enums` in place and lean on `forceUpdate()`, which a
   * memoised table cannot observe.
   */
  writeEnums = (typeOption: IPropertyTypeOptions, enums: IPropertyEnums[]): void => {
    const properties: IProperty[] = [...this.props.properties];
    const selectedType: IPropertyTypeOptions | undefined = properties[this.props.idx].typeOptions.find(
      (t) => t.propertyType === typeOption.propertyType
    );
    if (!selectedType) return;
    selectedType.enums = enums;
    properties[this.props.idx] = { ...properties[this.props.idx] };
    this.props.triggerPropertiesSetState(properties);
  };

  addPropertyEnum = (item: IPropertyTypeOptions): void => {
    if (!(this.state.enumKey.length > 0 && this.state.enumValue.length > 0)) return;
    const currentEnums: IPropertyEnums[] = item.enums ?? [];
    const exists: IPropertyEnums | undefined = currentEnums.find((x) => x.key === this.state.enumKey);
    let newEnums: IPropertyEnums[] = [];
    if (exists) {
      newEnums = currentEnums.map((x) => (x.key === this.state.enumKey ? { ...x, value: this.state.enumValue } : x));
    } else {
      newEnums = [...currentEnums, { key: this.state.enumKey, value: this.state.enumValue }];
    }
    this.writeEnums(item, newEnums);
    this.setState({ enumKey: "", enumValue: "" });
  };

  deleteEnumFromJson = (item: IPropertyEnums, pItem: IPropertyTypeOptions): void => {
    this.writeEnums(pItem, pItem.enums?.filter((x) => x.key !== item.key) ?? []);
  };

  handleOwnJSONTypeChange = (i: number, typeOption: IPropertyTypeOptions): void => {
    const properties: IProperty[] = [...this.props.properties];
    const selectedType: IPropertyTypeOptions | undefined = properties[i].typeOptions.find(
      (t) => t.propertyType === typeOption.propertyType
    );
    if (selectedType) {
      selectedType.hasOwnJSONSchema = !typeOption.hasOwnJSONSchema;
    }
    this.props.triggerPropertiesSetState(properties);
  };

  handleOptionsChange = (i: number, typeOption: IPropertyTypeOptions): void => {
    const properties: IProperty[] = [...this.props.properties];
    const selectedType: IPropertyTypeOptions | undefined = properties[i].typeOptions.find(
      (t) => t.propertyType === typeOption.propertyType
    );
    if (selectedType) {
      selectedType.hasOptions = !typeOption.hasOptions;
    }
    this.props.triggerPropertiesSetState(properties);
  };

  handlePropertyOptions = (i: number, item: IPropertyTypeOptions, rawValue: string): void => {
    const properties: IProperty[] = [...this.props.properties];
    const value: string[] = rawValue.split("\n").filter((x) => x !== "");
    const { typeOptions } = properties[i];
    const selectedType: IPropertyTypeOptions | undefined = typeOptions.find(
      (t) => t.propertyType === item.propertyType
    );
    if (selectedType) {
      selectedType.options = value;
    }
    properties[i] = { ...properties[i] };
    this.props.triggerPropertiesSetState(properties);
  };

  handlePropertyJsonChange = (i: number, item: IPropertyTypeOptions, value: string): void => {
    const properties: IProperty[] = [...this.props.properties];
    const { typeOptions } = properties[i];
    const selectedType: IPropertyTypeOptions | undefined = typeOptions.find(
      (t) => t.propertyType === item.propertyType
    );
    if (selectedType) {
      selectedType.jsonSchema = value;
    }
    properties[i] = { ...properties[i] };
    this.props.triggerPropertiesSetState(properties);
  };

  handleJSONMappedProperty = (idx: number, item: IPropertyTypeOptions, mappedPropertyName: string): void => {
    const properties: IProperty[] = [...this.props.properties];
    const { typeOptions } = properties[idx];
    const selectedType: IPropertyTypeOptions | undefined = typeOptions.find(
      (t) => t.propertyType === item.propertyType
    );
    if (selectedType) {
      selectedType.connectedPropertyName = mappedPropertyName;
    }
    properties[idx] = { ...properties[idx] };
    this.props.triggerPropertiesSetState(properties);
  };

  handleJSONMappingFields = (idx: number, item: IPropertyTypeOptions, propertyName: string, value: string): void => {
    const properties: IProperty[] = [...this.props.properties];
    const { typeOptions } = properties[idx];
    const selectedType: IPropertyTypeOptions | undefined = typeOptions.find(
      (t) => t.propertyType === item.propertyType
    );
    if (selectedType) {
      if (propertyName === "path") {
        selectedType.mappingPath = value;
      } else if (propertyName === "extra") {
        selectedType.extraJSONItems = value;
      }
    }
    properties[idx] = { ...properties[idx] };
    this.props.triggerPropertiesSetState(properties);
  };

  isReactProperty(key: string): boolean {
    if (ReactTypes.includes(EPropertyType[key])) return true;
    return false;
  }

  /** Stable per-property id so a field's `<label>` can point at its control. */
  getFieldId = (key: string): string => {
    return `componentProperty-${this.props.idx}-${key}`;
  };

  renderFieldHint = (message: string): React.ReactNode => {
    return (
      <KuikaTooltip content={message}>
        <span className="kuika-component-property__hint">
          <KuikaSvg name="info-circle" width={14} height={14} color="var(--dark-6)" />
        </span>
      </KuikaTooltip>
    );
  };

  /**
   * The modal renders through a portal, so it sits outside the wizard's
   * `<Form layout="vertical">` in the DOM. antd's vertical layout is a
   * descendant rule on that `<form>` element, so an antd `Form.Item` in here
   * silently fell back to the horizontal layout and every label reserved its
   * own text width — leaving the six controls starting at six different x
   * positions. Owning the label markup keeps the column edges aligned no
   * matter where the portal puts us, and lets the label actually point at its
   * control (antd emitted a `for`-less label because these items are unnamed).
   */
  renderField = (
    fieldId: string,
    label: string,
    control: React.ReactNode,
    hint?: string,
    wide?: boolean
  ): React.ReactNode => {
    const className: string = wide
      ? "kuika-component-property__field kuika-component-property__field--wide"
      : "kuika-component-property__field";
    return (
      <div className={className}>
        <div className="kuika-component-property__label">
          <label className="kuika-component-property__label-text" htmlFor={fieldId}>
            {label}
          </label>
          {hint ? this.renderFieldHint(hint) : null}
        </div>
        {control}
      </div>
    );
  };

  renderDataRoleField = (): React.ReactNode => {
    const { p, idx } = this.props;
    const activeFlags: DataRoleFlag[] = this.getActiveDataRoleFlags(p);
    const options: KuikaSelectboxOption[] = [
      { value: DATA_ROLE_NONE, label: "None" },
      ...DATA_ROLE_FLAGS.map((flag: DataRoleFlag): KuikaSelectboxOption => ({ value: flag.key, label: flag.label }))
    ];
    const fieldId: string = this.getFieldId("dataRole");
    return this.renderField(
      fieldId,
      "Data role",
      <>
        <KuikaSelectbox
          id={fieldId}
          data-testid="componentPropertyDataRoleSelect"
          searchable
          placeholder="None"
          options={options}
          value={this.getSelectedDataRole(p)}
          onChange={(value) => this.handleDataRoleChange(idx, String(value))}
        />
        {activeFlags.length > 1 && (
          <div className="kuika-component-property__warning" data-testid="componentPropertyDataRoleConflictWarning">
            <KuikaSvg name="info-circle" width={14} height={14} color="var(--red-5)" />
            <KuikaSmallText>
              {`This property carries more than one role (${activeFlags
                .map((flag) => flag.label)
                .join(", ")}). Only "${activeFlags[0].label}" is honoured. Picking a role clears the others.`}
            </KuikaSmallText>
          </div>
        )}
      </>,
      "Which options bag this property is. A property can only be one — every consumer picks the first match and ignores the rest.",
      true
    );
  };

  renderToggleGroups = (): React.ReactNode => {
    const { p, idx } = this.props;
    return (
      <div className="kuika-component-property__groups">
        {PROPERTY_TOGGLE_GROUPS.map((group: PropertyToggleGroup) => (
          <section className="kuika-component-property__group" key={group.title}>
            <KuikaSmallBoldText className="kuika-component-property__group-title">{group.title}</KuikaSmallBoldText>
            {group.toggles.map((toggle: PropertyToggle) => (
              <div className="kuika-component-property__toggle" key={toggle.key}>
                <div className="kuika-component-property__toggle-text">
                  <KuikaSmallText>{toggle.label}</KuikaSmallText>
                  <KuikaSmallText className="kuika-component-property__toggle-description">
                    {toggle.description}
                  </KuikaSmallText>
                </div>
                <KuikaSwitch
                  dataTestId={`componentProperty${toggle.key.charAt(0).toUpperCase()}${toggle.key.slice(1)}Switch`}
                  labelPosition="none"
                  checked={p[toggle.key] === true}
                  onChange={(checked: boolean) => this.handlePropertyToggleChange(idx, toggle.key, checked)}
                />
              </div>
            ))}
          </section>
        ))}
      </div>
    );
  };

  renderEnumTable = (pItem: IPropertyTypeOptions): React.ReactNode => {
    const columns: KuikaListTableColumn<IPropertyEnums>[] = [
      {
        id: "key",
        header: () => "Enum Key",
        headerTestId: "componentPropertyEnumKeyHeader",
        accessorFn: (row: IPropertyEnums) => row.key,
        cell: (row: IPropertyEnums) => row.key
      },
      {
        id: "value",
        header: () => "Enum Value",
        headerTestId: "componentPropertyEnumValueHeader",
        accessorFn: (row: IPropertyEnums) => row.value,
        cell: (row: IPropertyEnums) => row.value
      }
    ];
    return (
      <KuikaListTable<IPropertyEnums>
        data-testid="componentPropertyEnumTable"
        data={pItem.enums ?? []}
        columns={columns}
        rowKey={(row: IPropertyEnums) => row.key}
        rowActionsTestId={(row: IPropertyEnums) => `componentPropertyEnumRowActions-${row.key}`}
        rowActions={[
          {
            key: "delete",
            label: "Delete",
            danger: true,
            dataTestId: "componentPropertyEnumDeleteAction",
            icon: <KuikaSvg name="trash" width={14} height={14} />,
            onSelect: (row: IPropertyEnums) => this.deleteEnumFromJson(row, pItem)
          }
        ]}
        emptyTitle="No enum values"
        emptyDescription="Add a key and a value above."
      />
    );
  };

  renderTypeOption = (pItem: IPropertyTypeOptions): React.ReactNode => {
    const { idx, properties } = this.props;
    const propertyType: string = this.getPropertyType(pItem);
    return (
      <div className="kuika-component-property__type-option" key={`pto_${pItem.propertyType}`}>
        <div className="kuika-component-property__type-option-header">
          <KuikaSmallBoldText>
            {KMainFunctions.convertCamelCaseToFriendlyName(EPropertyType[pItem.propertyType])}
          </KuikaSmallBoldText>

          {propertyType === "json" && (
            <div className="kuika-component-property__inline-toggle">
              <KuikaSmallText>Does it have it&apos;s own JSON Schema?</KuikaSmallText>
              <KuikaSwitch
                dataTestId="componentPropertyHasOwnJsonSchemaSwitch"
                labelPosition="none"
                checked={pItem.hasOwnJSONSchema === true}
                onChange={() => this.handleOwnJSONTypeChange(idx, pItem)}
              />
            </div>
          )}

          {propertyType === "other" && (
            <div className="kuika-component-property__inline-toggle">
              <KuikaSmallText>Does it have specific options?</KuikaSmallText>
              <KuikaSwitch
                dataTestId="componentPropertyHasOptionsSwitch"
                labelPosition="none"
                checked={pItem.hasOptions === true}
                onChange={() => this.handleOptionsChange(idx, pItem)}
              />
            </div>
          )}
        </div>

        {propertyType === "column" && (
          <div className="kuika-component-property__device-row">
            <KuikaSmallText>Select a device for this property:</KuikaSmallText>
            <div className="kuika-component-property__device-group">
              {DEVICE_CHOICES.map((choice: DeviceChoice) => {
                const selected: boolean = pItem.deviceTypeForColumnAttribute === choice.device;
                return (
                  <button
                    type="button"
                    key={choice.label}
                    data-testid={`componentPropertyDeviceButton-${choice.label}`}
                    data-selected={selected}
                    className={`kuika-component-property__device-button${
                      selected ? " kuika-component-property__device-button--selected" : ""
                    }`}
                    onClick={() => this.handleDeviceChangedForColumnAttribute(choice.device, idx, pItem)}
                  >
                    <KuikaSvg name={choice.icon} width={16} height={16} color="currentColor" />
                    <KuikaSmallText>{choice.label}</KuikaSmallText>
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {propertyType === "enum" && (
          <div className="kuika-component-property__enum">
            <div className="kuika-component-property__enum-form">
              <KuikaInput
                data-testid="componentPropertyEnumKeyInput"
                placeholder="Enum Key"
                value={this.state.enumKey}
                onChange={(_e, value) => this.handleEnumKeyChange(String(value))}
              />
              <KuikaInput
                data-testid="componentPropertyEnumValueInput"
                placeholder="Enum Value"
                value={this.state.enumValue}
                onChange={(_e, value) => this.handleEnumValueChange(String(value))}
              />
              <KuikaButton
                data-testid="componentPropertyEnumAddButton"
                type="primary"
                onClick={() => this.addPropertyEnum(pItem)}
              >
                Add
              </KuikaButton>
            </div>
            {this.renderEnumTable(pItem)}
          </div>
        )}

        {pItem.hasOptions === true && (
          <KuikaInput
            data-testid="componentPropertyOptionsInput"
            type="textarea"
            rows={3}
            placeholder="Please enter each option in a new line"
            defaultValue={pItem.options?.join("\n")}
            onChange={(_e, value) => this.handlePropertyOptions(idx, pItem, String(value))}
          />
        )}

        {pItem.hasOwnJSONSchema === true && (
          <KuikaInput
            data-testid="componentPropertyJsonSchemaInput"
            type="textarea"
            rows={3}
            placeholder="Please enter the Json schema"
            defaultValue={pItem.jsonSchema}
            onChange={(_e, value) => this.handlePropertyJsonChange(idx, pItem, String(value))}
          />
        )}

        {pItem.hasOwnJSONSchema === false && pItem.propertyType === EPropertyType.object.toString() && (
          <div className="kuika-component-property__json-mapping">
            <KuikaSelectbox
              data-testid="componentPropertyJsonMappedPropertySelect"
              placeholder="Property to map it with."
              value={pItem.connectedPropertyName}
              options={properties.map((pp: IProperty): KuikaSelectboxOption => ({ value: pp.name, label: pp.name }))}
              onChange={(value) => this.handleJSONMappedProperty(idx, pItem, String(value))}
            />
            <KuikaInput
              data-testid="componentPropertyJsonMappingPathInput"
              defaultValue={pItem.mappingPath}
              placeholder="Please enter JSON mapping path."
              onChange={(_e, value) => this.handleJSONMappingFields(idx, pItem, "path", String(value))}
            />
            <KuikaInput
              data-testid="componentPropertyJsonExtraFieldsInput"
              defaultValue={pItem.extraJSONItems}
              placeholder="Please enter extra JSON fields."
              onChange={(_e, value) => this.handleJSONMappingFields(idx, pItem, "extra", String(value))}
            />
          </div>
        )}
      </div>
    );
  };

  render() {
    const { p, idx } = this.props;
    const propertyTypeKeys: string[] = Object.keys(EPropertyType).filter(
      (key: string) => isNaN(Number(key)) && this.isReactProperty(key)
    );
    const defaultTypeOptions: KuikaSelectboxOption[] = Object.keys(EPropertyType)
      .filter((key: string) => isNaN(Number(key)))
      .map((key: string): KuikaSelectboxOption => ({
        value: EPropertyType[key],
        label: KMainFunctions.convertCamelCaseToFriendlyName(key),
        disabled: p?.type?.indexOf(EPropertyType[key]) === -1
      }));
    const typeOptions: IPropertyTypeOptions[] = [...(p.typeOptions ?? [])];

    return (
      <KModal
        className="kuika-component-property"
        showModal={this.props.showModal}
        closeModal={this.closeModal}
        header={p?.name}
        width={960}
        centered
        dontRenderSpinner
        footer={
          <KModalFooter submitLabel="Done" submitTestId="componentPropertyDoneButton" onSubmit={this.closeModal} />
        }
      >
        <div className="kuika-component-property__body" data-testid="componentPropertiesModal">
          <div className="kuika-component-property__grid">
            {this.renderField(
              this.getFieldId("name"),
              "Attribute Name",
              <KuikaInput
                id={this.getFieldId("name")}
                data-testid="componentPropertyAttributeNameInput"
                defaultValue={p.name}
                placeholder="ex: sizec,color.."
                onChange={(_e, value) => this.handlePropertyTextChange(idx, "name", String(value))}
              />,
              "Property name is component's physical property name."
            )}

            {this.renderField(
              this.getFieldId("tagName"),
              "Display Name",
              <KuikaInput
                id={this.getFieldId("tagName")}
                data-testid="componentPropertyDisplayNameInput"
                defaultValue={p.tagName}
                onKeyPress={OnPressKeyRules.handlePressTagNameKey}
                placeholder="ex: sizec,color.."
                onChange={(_e, value) => this.handlePropertyTextChange(idx, "tagName", String(value))}
              />,
              "The property alias is alias of your component's property."
            )}

            {this.renderField(
              this.getFieldId("value"),
              "Default Value",
              <KuikaInput
                id={this.getFieldId("value")}
                data-testid="componentPropertyDefaultValueInput"
                type="textarea"
                rows={3}
                defaultValue={this.getPropertySymbolValue(p)}
                placeholder="ex: 200px, red.."
                onChange={(_e, value) => this.handlePropertyTextChange(idx, "value", String(value))}
              />,
              "This field is the default value for a property."
            )}

            {this.renderField(
              this.getFieldId("description"),
              "Description",
              <KuikaInput
                id={this.getFieldId("description")}
                data-testid="componentPropertyDescriptionInput"
                type="textarea"
                rows={3}
                defaultValue={p.description}
                placeholder="Please enter the description"
                onChange={(_e, value) => this.handlePropertyTextChange(idx, "description", String(value))}
              />,
              "Property Description: is a way to describe for your users what does this component do."
            )}

            {this.renderField(
              this.getFieldId("appSupportType"),
              "App Support Type",
              <KuikaSelectbox
                id={this.getFieldId("appSupportType")}
                data-testid="componentPropertyAppSupportTypeSelect"
                searchable
                placeholder="Please select a support type"
                options={APP_SUPPORT_TYPE_OPTIONS}
                value={p.appSupportType ?? EAppSupportType.All}
                onChange={(value) => this.handleAppSupportTypeChange(idx, value as EAppSupportType)}
              />
            )}

            {p.type &&
              p.type.length > 1 &&
              this.renderField(
                this.getFieldId("selectedType"),
                "Default Type",
                <KuikaSelectbox
                  id={this.getFieldId("selectedType")}
                  data-testid="componentPropertyDefaultTypeSelect"
                  placeholder="Select default type."
                  options={defaultTypeOptions}
                  value={p.selectedType}
                  onChange={(value) => this.handlePropertySelectedTypeChange(idx, value as EPropertyType)}
                />,
                "Property default type: When you have more than one type, you need to select the default type you would like to use."
              )}

            {this.renderDataRoleField()}
          </div>

          {this.renderToggleGroups()}

          <section className="kuika-component-property__section">
            <KuikaSmallBoldText className="kuika-component-property__section-title">Property Types</KuikaSmallBoldText>
            <div className="kuika-component-property__type-grid">
              {propertyTypeKeys.map((key: string) => (
                <KuikaCheckbox
                  key={`pt__${key}`}
                  dataTestId={`componentPropertyTypeCheckbox-${key}`}
                  label={KMainFunctions.convertCamelCaseToFriendlyName(key)}
                  checked={p?.type?.indexOf(EPropertyType[key]) > -1}
                  onChange={(e) => this.handleTypeChange(idx, EPropertyType[key], e.target.checked)}
                />
              ))}
            </div>
          </section>

          {typeOptions.length > 0 && (
            <div className="kuika-component-property__type-options">
              {typeOptions.map((pItem: IPropertyTypeOptions) => this.renderTypeOption(pItem))}
            </div>
          )}
        </div>
      </KModal>
    );
  }
}

const componentPropertiesModal = withContext<ComponentPropertiesModalProps>(ComponentPropertiesModal);
export { componentPropertiesModal as ComponentPropertiesModal };
